import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import {
  ArrowForward,
  CalendarToday,
  LocationOn,
  Settings,
  WaterDrop,
} from '@mui/icons-material';

import industrial from './Assets/Industrial.jpeg';
import residential from './Assets/residential.jpeg';
import img3 from './Assets/Installation3.webp';
import estate from './Assets/estate.jpeg';
import img5 from './Assets/Installation1.webp';
import img6 from './Assets/Installation3.webp';

import Navbar from './Navbar';
import Footer from './Footer';
import { Helmet } from 'react-helmet-async';

const Main = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f6f9fc;
  color: #0f172a;
`;

const Container = styled.main`
  flex: 1;
  padding-top: 76px;
`;

const Hero = styled.section`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background:
    radial-gradient(circle at 82% 18%, rgba(14, 165, 233, 0.34), transparent 34%),
    linear-gradient(135deg, #06111f 0%, #0b1f3a 45%, #075985 100%);
  color: white;
`;

const HeroInner = styled.div`
  width: min(1180px, calc(100% - 36px));
  margin: 0 auto;
  padding: 76px 0 60px;
  text-align: center;
`;

const Eyebrow = styled.div`
  width: fit-content;
  margin: 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 13px;
  border: 1px solid rgba(186, 230, 253, 0.38);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: #bae6fd;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
`;

const Heading = styled.h1`
  max-width: 760px;
  margin: 20px auto 18px;
  font-size: clamp(2.2rem, 5vw, 3.6rem);
  line-height: 1.08;
  font-weight: 800;
  letter-spacing: 0;
`;

const HeroText = styled.p`
  max-width: 660px;
  margin: 0 auto;
  color: #dbeafe;
  font-size: clamp(1rem, 1.7vw, 1.15rem);
  line-height: 1.8;
`;

const Section = styled.section`
  width: min(1180px, calc(100% - 36px));
  margin: 0 auto;
  padding: 70px 0 40px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 26px;
`;

const Card = styled(motion.article)`
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  background: white;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.07);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 26px 60px rgba(15, 23, 42, 0.12);
  }

  &:hover img {
    transform: scale(1.06);
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

const Image = styled.img`
  display: block;
  width: 100%;
  height: 240px;
  object-fit: cover;
  transition: transform 0.5s ease;
`;

const Tag = styled.span`
  position: absolute;
  top: 14px;
  left: 14px;
  padding: 6px 12px;
  border-radius: 999px;
  background: #2563eb;
  color: white;
  font-size: 0.76rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Info = styled.div`
  padding: 22px;
`;

const Location = styled.h3`
  margin: 0 0 8px;
  color: #0f172a;
  font-size: 1.1rem;
  font-weight: 800;
`;

const Description = styled.p`
  margin: 0 0 16px;
  color: #64748b;
  font-size: 0.94rem;
  line-height: 1.65;
`;

const Meta = styled.div`
  display: grid;
  gap: 8px;
  padding-top: 14px;
  border-top: 1px solid #e2e8f0;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #475569;
  font-size: 0.88rem;

  svg {
    font-size: 1rem;
    color: #0ea5e9;
  }
`;

const FinalCta = styled.section`
  width: min(1180px, calc(100% - 36px));
  margin: 40px auto 80px;
  padding: clamp(34px, 6vw, 58px);
  border-radius: 28px;
  background:
    radial-gradient(circle at 90% 20%, rgba(125, 211, 252, 0.22), transparent 32%),
    linear-gradient(135deg, #071428, #0f3a5f);
  color: white;
  text-align: center;

  h2 {
    max-width: 700px;
    margin: 0 auto;
    font-size: clamp(1.8rem, 3.6vw, 2.5rem);
    line-height: 1.15;
  }

  p {
    max-width: 600px;
    margin: 16px auto 26px;
    color: #dbeafe;
    line-height: 1.8;
  }
`;

const HeroActions = styled.div`
  display: flex;
  justify-content: center;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  min-height: 48px;
  padding: 0 22px;
  border: none;
  border-radius: 14px;
  background: #2563eb;
  color: white;
  font-size: 0.96rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
  box-shadow: 0 18px 35px rgba(37, 99, 235, 0.28);

  &:hover {
    background: #1d4ed8;
    transform: translateY(-2px);
    box-shadow: 0 22px 45px rgba(37, 99, 235, 0.34);
  }
`;

const installations = [
  {
    img: industrial,
     title: 'Industrial Factory',
    type: 'Industrial',
  
    desc: 'Industrial water monitoring system designed to detect leaks and optimize water usage across factory operations.',
  },
  {
    img: residential,
  title: 'Residential Apartments',
    type: 'Residential',
  
    desc: 'Installation of ultrasonic smart meters enabling real-time water consumption monitoring and automated billing.',
  },
  {
    img: img3,
    title: 'School Campus',
    type: 'Institution',
   
    desc: 'Centralized water monitoring system installed across dormitories, kitchens, and classrooms.',
  },
  {
    img: estate,
    title: 'Residential Estate',
    type: 'Residential',
   
    desc: 'Complete estate-wide smart water metering solution with automated billing integration.',
  },
  {
    img: img5,
    title: 'Hotel Installation',
    type: 'Commercial',
  
    desc: 'IoT-based water monitoring system helping the hotel optimize water consumption and reduce operational costs.',
  },
  {
    img: img6,
    title: 'Hospital Facility',
    type: 'Healthcare',
   
    desc: 'High reliability smart metering infrastructure deployed to ensure consistent monitoring of hospital water supply.',
  },
];

const Portfolio = () => {
  return (
    <Main>
      <Helmet>
        <title>Our Installations | AquaTools Portfolio</title>
        <meta
          name="description"
          content="See AquaTools smart water metering installations across residential, commercial, industrial, and institutional sites."
        />
        <meta name="keywords" content="water meter installations, smart metering projects, AquaTools portfolio" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Navbar />

      <Container>
        <Hero>
          <HeroInner>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55 }}
            >
              <Eyebrow>
                <WaterDrop fontSize="small" />
                Our Work
              </Eyebrow>
              <Heading>Field-Proven Installations</Heading>
              <HeroText>
                AquaTools delivers advanced smart water metering solutions across residential,
                commercial, industrial, and institutional facilities — enabling real-time monitoring,
                automated billing, leak detection, and efficient water management.
              </HeroText>
            </motion.div>
          </HeroInner>
        </Hero>

        <Section>
          <Grid>
            {installations.map((item, index) => (
              <Card
                key={`${item.title}-${index}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
              >
                <ImageWrapper>
                  <Image src={item.img} alt={item.title} />
                  <Tag>{item.type}</Tag>
                </ImageWrapper>

                <Info>
                  <Location>{item.title}</Location>
                  <Description>{item.desc}</Description>

               
                </Info>
              </Card>
            ))}
          </Grid>
        </Section>

        <FinalCta>
          <h2>Planning a metering project of your own?</h2>
          <p>
            From single sites to multi-estate rollouts, our team can help design and deploy a smart
            metering solution that fits your scale.
          </p>
          <HeroActions>
            <StyledLink to="/contactus">
              <Button>
                Start a conversation
                <ArrowForward fontSize="small" />
              </Button>
            </StyledLink>
          </HeroActions>
        </FinalCta>
      </Container>

      <Footer />
    </Main>
  );
};

export default Portfolio;
